import { useRef, useEffect, useState } from 'react'
import { motion, useInView } from 'framer-motion'

const checklistItems = [
  'Stress e attivazione',
  'Energia giornaliera',
  'Qualità del sonno',
  'Focus sostenuto',
  'Abitudini attuali',
  'Blocco quotidiani',
  'Postura e corpo',
]

export default function AnalysisSection() {
  const sectionRef = useRef(null)
  const isInView = useInView(sectionRef, { once: true, margin: '-100px' })
  const [scanProgress, setScanProgress] = useState(0)
  const [scannedItems, setScannedItems] = useState(0)

  useEffect(() => {
    if (!isInView) return
    let progress = 0
    const interval = setInterval(() => {
      progress += 1.5
      if (progress >= 100) {
        progress = 100
        clearInterval(interval)
      }
      setScanProgress(progress)
      setScannedItems(Math.floor((progress / 100) * checklistItems.length))
    }, 50)
    return () => clearInterval(interval)
  }, [isInView])

  return (
    <section
      id="analisi"
      ref={sectionRef}
      className="relative w-full py-24 md:py-32 px-4"
      style={{ background: '#050510' }}
    >
      <div className="max-w-5xl mx-auto">
        <motion.div
          className="nerd-card rounded-2xl p-8 md:p-12 relative overflow-hidden"
          initial={{ opacity: 0, y: 40 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
        >
          {/* Scan line animation */}
          {isInView && scanProgress < 100 && (
            <motion.div
              className="absolute left-0 right-0 h-[2px] z-20 pointer-events-none"
              style={{
                background: 'linear-gradient(90deg, transparent, #00d4ff, transparent)',
                top: `${scanProgress}%`,
              }}
              animate={{ opacity: [0.6, 1, 0.6] }}
              transition={{ duration: 0.5, repeat: Infinity }}
            />
          )}

          {/* Corner brackets */}
          <div className="absolute top-4 left-4 w-6 h-6 border-l-2 border-t-2 border-nerd-purple/40" />
          <div className="absolute top-4 right-4 w-6 h-6 border-r-2 border-t-2 border-nerd-purple/40" />
          <div className="absolute bottom-4 left-4 w-6 h-6 border-l-2 border-b-2 border-nerd-purple/40" />
          <div className="absolute bottom-4 right-4 w-6 h-6 border-r-2 border-b-2 border-nerd-purple/40" />

          <div className="grid md:grid-cols-2 gap-10 items-center">
            {/* Left: Text */}
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-2 h-2 rounded-full bg-nerd-blue animate-pulse" />
                <span className="text-[10px] text-nerd-blue uppercase tracking-widest font-display">System Scan</span>
              </div>

              <h2 className="font-display text-2xl sm:text-3xl md:text-4xl font-bold text-nerd-white mb-4">
                Prima di allenarti di più, capiamo dove il{' '}
                <span className="text-gradient">sistema si blocca.</span>
              </h2>

              <p className="text-nerd-gray text-base leading-relaxed mb-6">
                Una sessione di analisi per leggere stress, energia, focus, sonno, abitudini, corpo e blocchi quotidiani. Da lì costruiamo il primo piano d'azione.
              </p>

              <a href="#" className="nerd-btn-primary inline-block text-sm">
                Prenota l'analisi
              </a>

              <p className="text-nerd-gray/60 text-xs mt-4">
                30-40 minuti. Online. Pratica. Nessuna teoria inutile.
              </p>
            </div>

            {/* Right: HUD Panel */}
            <div className="bg-nerd-dark/60 rounded-xl p-6 border border-nerd-purple/10">
              {/* Progress bar */}
              <div className="mb-6">
                <div className="flex justify-between text-[10px] text-nerd-gray uppercase tracking-wider font-display mb-2">
                  <span>System Scan</span>
                  <span className="text-nerd-cyan">{Math.round(scanProgress)}%</span>
                </div>
                <div className="h-1.5 bg-nerd-bg rounded-full overflow-hidden">
                  <motion.div
                    className="h-full rounded-full"
                    style={{
                      background: 'linear-gradient(90deg, #8b5cf6, #00d4ff)',
                      width: `${scanProgress}%`,
                    }}
                  />
                </div>
              </div>

              {/* Checklist */}
              <div className="space-y-3">
                {checklistItems.map((item, i) => (
                  <motion.div
                    key={item}
                    className="flex items-center gap-3"
                    initial={{ opacity: 0, x: -10 }}
                    animate={isInView && i < scannedItems ? { opacity: 1, x: 0 } : { opacity: 0.3, x: 0 }}
                    transition={{ duration: 0.3, delay: i * 0.1 }}
                  >
                    <div
                      className="w-5 h-5 rounded border flex items-center justify-center flex-shrink-0 transition-colors duration-300"
                      style={{
                        borderColor: i < scannedItems ? '#00d4ff' : 'rgba(139,92,246,0.3)',
                        background: i < scannedItems ? 'rgba(0,212,255,0.1)' : 'transparent',
                      }}
                    >
                      {i < scannedItems && (
                        <svg viewBox="0 0 24 24" fill="none" stroke="#00d4ff" strokeWidth="3" className="w-3 h-3">
                          <path d="M5 12l5 5L20 7" />
                        </svg>
                      )}
                    </div>
                    <span
                      className="text-sm transition-colors duration-300"
                      style={{
                        color: i < scannedItems ? '#f5f5f5' : '#9ca3af',
                      }}
                    >
                      {item}
                    </span>
                  </motion.div>
                ))}
              </div>

              {/* Status */}
              <div className="mt-6 pt-4 border-t border-nerd-purple/10 flex items-center justify-between">
                <span className="text-[10px] text-nerd-gray uppercase tracking-wider font-display">
                  Stato: {scanProgress >= 100 ? 'Completato' : 'Scansione in corso...'}
                </span>
                <div className="flex gap-1">
                  {[0, 1, 2].map((dot) => (
                    <div
                      key={dot}
                      className="w-1.5 h-1.5 rounded-full"
                      style={{
                        background: scanProgress >= 100 ? '#00d4ff' : '#8b5cf6',
                        opacity: scanProgress >= 100 ? 1 : 0.3 + dot * 0.35,
                      }}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  )
}
